import { useEffect } from 'react';
import { Award, Flame, PartyPopper, Trophy } from 'lucide-react';
import type { CompleteStepResult } from '../types';
import Mark from './Mark';

/**
 * The delight moment — the one screen the whole product is built to earn. Shows
 * the Zor just earned (terracotta, animated), the Kasrat streak, and, on the
 * last step, the trail-complete + badge celebration. This is where terracotta is
 * meant to sing.
 */
export default function RewardMoment({
  result,
  stepTitle,
  onClose,
  onSeeDangal,
}: {
  result: CompleteStepResult;
  stepTitle: string;
  onClose: () => void;
  /** Omitted for candidates — they have no Dangal, so the CTA + copy are hidden. */
  onSeeDangal?: () => void;
}) {
  // Esc closes.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && onClose();
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const { awarded, streak, trackComplete, points } = result;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-ink-900/50 p-6 backdrop-blur-sm"
      role="dialog"
      aria-modal="true"
      onClick={onClose}
    >
      <div
        className="w-full max-w-sm animate-rise-in rounded-2xl border border-terracotta-200 bg-paper-0 p-7 text-center shadow-reward dark:border-terracotta-700 dark:bg-clay-raised"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mx-auto flex h-16 w-16 animate-glow items-center justify-center rounded-2xl bg-terracotta-500">
          {trackComplete ? (
            <PartyPopper className="h-8 w-8 text-terracotta-50" />
          ) : (
            <Mark className="h-9 w-9 text-terracotta-50" />
          )}
        </div>

        <p className="mt-5 text-sm font-medium text-ink-500 dark:text-paper-300">
          {result.alreadyDone ? 'Already in the books' : 'Zor earned'}
        </p>
        <p className="mt-1 inline-block animate-rep-pop text-5xl font-bold tracking-tight text-terracotta-500">
          +{awarded}
        </p>
        <p className="mt-1 truncate text-sm text-ink-400">{stepTitle}</p>

        <div className="mt-5 flex items-center justify-center gap-2">
          <span className="flex items-center gap-1 rounded-full bg-terracotta-50 px-3 py-1 text-sm font-semibold text-terracotta-700 ring-1 ring-inset ring-terracotta-200 dark:bg-terracotta-900/30 dark:text-terracotta-200 dark:ring-terracotta-700">
            <Trophy className="h-3.5 w-3.5" /> {points.month} Zor this month
          </span>
          {streak.count > 0 && (
            <span className="flex items-center gap-1 rounded-full bg-terracotta-500 px-3 py-1 text-sm font-semibold text-white">
              <Flame className="h-3.5 w-3.5" /> {streak.count}-day kasrat
            </span>
          )}
        </div>

        {trackComplete && (
          <div className="mt-4 flex items-center justify-center gap-2 rounded-xl bg-verdant-200/50 px-3 py-2 text-sm font-medium text-verdant-700 dark:bg-verdant-700/20 dark:text-verdant-200">
            <Award className="h-4 w-4" />
            Kushti complete{result.badge ? ' — badge earned!' : '!'}
          </div>
        )}

        {onSeeDangal && (
          <p className="mt-5 text-xs text-ink-400">It just landed on the Dangal — go see.</p>
        )}
        <div className={`flex gap-2 ${onSeeDangal ? 'mt-3' : 'mt-5'}`}>
          {onSeeDangal && (
            <button
              onClick={onSeeDangal}
              className="flex-1 rounded-xl bg-terracotta-500 px-4 py-2.5 text-sm font-semibold text-white shadow-sm transition hover:bg-terracotta-600"
            >
              See the Dangal
            </button>
          )}
          <button
            onClick={onClose}
            className={`rounded-xl px-4 py-2.5 text-sm font-medium transition ${
              onSeeDangal
                ? 'border border-rule text-ink-600 hover:bg-paper-100 dark:border-clay-rule dark:text-paper-200 dark:hover:bg-clay-raised'
                : 'flex-1 bg-terracotta-500 font-semibold text-white shadow-sm hover:bg-terracotta-600'
            }`}
          >
            Keep training
          </button>
        </div>
      </div>
    </div>
  );
}
